import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useId, useState } from 'react';

import {
  ArrowRightStrokeIcon,
  Button,
  BUTTON_SLOTS,
  Divider,
  EyeOffStrokeIcon,
  EyeStrokeIcon,
  GoogleButton,
  Heading,
  IconButton,
  LabeledInput,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Text,
  TextButton,
  TextField,
  TOAST_TIMEOUT,
  minDelay,
  LINK_SLOTS,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { RouterTextButtonLink } from '@/libs/tanstack-router/components/router-text-button-link.component';
import { authMutations } from '@/resources/auth/auth.api';
import { userMutations } from '@/resources/user/user.api';
import { UserSchema } from '@/resources/user/user.schema';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_auth/sign-in')({
  component: RouteComponent,
  beforeLoad: () => {
    if (localStorage.getItem('access_token')) {
      throw redirect({ to: '/' });
    }
  },
});

function RouteComponent() {
  const id = useId();
  const navigate = Route.useNavigate();
  const { toastQueue } = Route.useRouteContext();
  const match = Route.useMatch();

  const [isPasswordFieldVisible, setIsPasswordFieldVisible] = useState(false);
  const [isEnteredPasswordVisible, setIsEnteredPasswordVisible] = useState(false);

  const loginMutation = useMutation(authMutations.login);
  const recoverPasswordMutation = useMutation(userMutations.recoverPassword);
  const googleLoginMutation = useMutation(authMutations.googleLogin);

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      const { data, error } = await minDelay(tryCatch(loginMutation.mutateAsync(value)), PROGRESS_INDICATOR_MIN_DELAY_DURATION);
      if (!error) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);

        await navigate({ to: '/' });
      }
    },
  });

  return (
    <>
      <div {...stylex.props(styles.base)}>
        <div {...stylex.props(formStyles.container)}>
          <form
            id={id}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // To prevent the form from being submitted when the Enter key is pressed while the password field is not yet displayed
              if (isPasswordFieldVisible) {
                form.handleSubmit();
              }
            }}
            {...stylex.props(formStyles.base)}>
            <Heading level={1} size={28}>Sign in</Heading>
            <form.Field name="email" validators={{ onMount: UserSchema.email, onChange: UserSchema.email }}>
              {(field) =>
                <TextField
                  autoComplete="off"
                  id={field.name}
                  // The submit button is disabled until the correct value is entered, so no error state is required
                  isInvalid={false}
                  name={field.name}
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    field.handleChange(value);

                    if (field.state.meta.errors.length) {
                      setIsPasswordFieldVisible(false);
                      form.resetField('password');
                    }
                  }}>
                  <LabeledInput label="Email" />
                </TextField>}
            </form.Field>

            {isPasswordFieldVisible &&
              <div {...stylex.props(passwordFieldStyles.container)}>
                <form.Field
                  name="password"
                  validators={{ onMount: UserSchema.password, onChange: UserSchema.password }}>
                  {(field) =>
                    <TextField
                      autoComplete="off"
                      autoFocus
                      id={field.name}
                      // The submit button is disabled until the correct value is entered, so no error state is required
                      isInvalid={false}
                      name={field.name}
                      type={isEnteredPasswordVisible ? 'text' : 'password'}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}>
                      <LabeledInput label="Password">
                        {(renderProps) =>
                          <>
                            {renderProps.defaultChildren}
                            {field.state.value.length > 0 &&
                              <IconButton
                                aria-label="Show entered password"
                                excludeFromTabOrder
                                size={32}
                                variant="ghost"
                                onPress={() => setIsEnteredPasswordVisible(!isEnteredPasswordVisible)}>
                                {isEnteredPasswordVisible ? <EyeStrokeIcon /> : <EyeOffStrokeIcon />}
                              </IconButton>}
                          </>}
                      </LabeledInput>
                    </TextField>}
                </form.Field>

                <form.Subscribe selector={(state) => state.values.email}>
                  {(email) =>
                    <TextButton
                      style={passwordFieldStyles.resetButton}
                      onPress={() => recoverPasswordMutation.mutate(
                        { email },
                        { onSuccess: () => toastQueue.add({ message: 'Check your email inbox and click on the link we sent to reset your password.', variant: 'success' }, { timeout: TOAST_TIMEOUT }) })}>
                      <Text size={16}>Reset password</Text> {/* This button has custom text size, so Text component is used for customization */}
                    </TextButton>}
                </form.Subscribe>
              </div>}
          </form>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting, !state.fieldMeta.email?.errors?.length] as const}>
            {([canSubmit, isSubmitting, isValidEmail]) =>
              isPasswordFieldVisible ?
                <Button
                  form={id}
                  isDisabled={!canSubmit}
                  isPending={isSubmitting}
                  isRounded
                  size={48}
                  type="submit">
                  Sign in
                </Button> :
                <Button
                  isDisabled={!isValidEmail}
                  isRounded
                  size={48}
                  onPress={() => setIsPasswordFieldVisible(true)}>
                  <Text>Continue</Text>
                  <ArrowRightStrokeIcon slot={BUTTON_SLOTS.RIGHT} />
                </Button>}
          </form.Subscribe>
        </div>

        {!isPasswordFieldVisible &&
          <>
            <div {...stylex.props(styles.dividerContainer)}>
              <Divider size={0.5} variant="silent" />
              <Text color="tertiary" size={13}>or</Text>
              <Divider size={0.5} variant="silent" />
            </div>

            <GoogleButton
              isRounded
              size={52}
              variant="alternative"
              onPress={() => {
                sessionStorage.setItem('google_login_url', match.fullPath);
                googleLoginMutation.mutate(void 0, { onSuccess: (data) => window.location.href = data.auth_url });
              }}>
              Continue with Google
            </GoogleButton>

            <Text color="tertiary" size={16} style={typographyStyles.textAction}>
              Not a member yet?
              <RouterLink size={16} to="/sign-up">
                Create account
                <ArrowRightStrokeIcon color="interactive" size={16} slot={LINK_SLOTS.RIGHT_ACCESSORY} />
              </RouterLink>
            </Text>
          </>}
      </div>
      <footer {...stylex.props(footerStyles.base)}>
        <RouterTextButtonLink safeArea="square" size={28} target="_blank" to="/privacy-and-terms/$" variant="tertiary">Privacy and terms</RouterTextButtonLink>
      </footer>
    </>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 32,
    maxWidth: 432,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: {
      default: 120,
      [breakpoints.large]: 140,
    },
    width: '100%',
  },
  dividerContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
});

const passwordFieldStyles = stylex.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  resetButton: {
    paddingInline: 0,
    width: 'fit-content',
  }
});

const typographyStyles = stylex.create({
  textAction: {
    display: 'inline-flex',
    gap: 8,
  }
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  container: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  }
});

const footerStyles = stylex.create({
  base: {
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    bottom: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    paddingBlock: 2,
    width: '100%',
  },
});
